<template>
  <div
    v-if="message"
    :id="id"
    class="base-form-error"
    role="alert"
  >
    <BaseIcon
      id="warning"
      size="sm"
    />
    {{ message }}
  </div>
</template>

<script setup lang="ts">
import BaseIcon from './BaseIcon.vue';

/**
 * Form validation error message component
 *
 * Displays validation errors with icon and red styling. Only renders
 * when message is present. Includes role="alert" for screen reader
 * announcements. Automatically used by FormControl component.
 *
 * @example With FormControl (automatic)
 * ```vue
 * <FormControl
 *   v-model="email"
 *   type="email"
 *   label="Email"
 *   :error="errors.email"
 * />
 * <!-- FormControl renders BaseFormError internally -->
 * ```
 *
 * @example Manual usage with aria-describedby
 * ```vue
 * <BaseLabel htmlFor="username" required>Username</BaseLabel>
 * <BaseInput
 *   id="username"
 *   v-model="username"
 *   aria-describedby="username-error"
 * />
 * <BaseFormError
 *   id="username-error"
 *   :message="errors.username"
 * />
 * ```
 *
 * @example Conditional error display
 * ```vue
 * <BaseInput v-model="password" type="password" />
 * <BaseFormError :message="passwordTooShort ? 'Password must be 8+ characters' : undefined" />
 * <!-- Only renders when passwordTooShort is true -->
 * ```
 */
export interface BaseFormErrorProps {
  /**
   * Error message to display
   * Component only renders when message is provided
   * @default undefined (component hidden)
   */
  message?: string;

  /**
   * Unique element ID for aria-describedby association
   * Links error message to input for screen reader accessibility
   * @default undefined
   */
  id?: string;
}

defineProps<BaseFormErrorProps>();
</script>

<style>
.base-form-error {
  background: var(--color-error-100);
  display: flex;
  align-items: center;
  gap: var(--gap-xs);
  color: var(--color-error-400);
  font-size: var(--font-sm);
  line-height: 1.5;
  margin: calc(var(--gap-xs) * -1) 0 0;
  padding: var(--gap-xs) var(--gap-sm);
  border-radius: 0 0 var(--input-border-radius-right) var(--input-border-radius-left);
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    bottom: 100%;
    width: var(--input-border-radius-left);
    height: var(--input-border-radius-left);
    background: radial-gradient(
      circle at var(--circle-position),
      transparent var(--input-border-radius-left),
      var(--color-error-100) var(--input-border-radius-left)
    );
  }

  &::before {
    --circle-position: 100% 0%;
    left: 0;
  }

  &::after {
    --circle-position: 0% 0%;
    right: 0;
  }
}
</style>
